import React, {ReactNode, useEffect} from 'react';
import {Form, Col, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import useData from '@/utils/useData';
import SearchBox, {SearchBoxType} from '@/views/search/SearchBox';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {useStoreActions, useStoreState} from 'store';
import {AdditionalSearchFilter, CustomizeFormField} from 'entities/chartFilters';
import {serialize} from '@/views/storyboard/utils/helpers';
import {EventTextFieldOption} from 'entities/event';
import Select from 'react-select';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface SelectOption {
    label: ReactNode;
    value: string;
}

const SmartFilterBar = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const searchQuery = useStoreState((state) => state.search.searchQuery);

    const paramsSetter = useStoreState((state) => state.search.paramsSetter);

    const fieldToQuery = useStoreState((state) => state.search.fieldToQuery);
    const storeFieldToQuery = useStoreActions((state) => state.search.setFieldToQuery);

    const viewTypeQuery = useStoreState((state) => state.search.viewTypeQuery);

    const searchQueryAllFields = useStoreState((state) => state.search.searchQueryAllFields);
    const [customizeFormFieldChoices] = useData<CustomizeFormField[]>(window.URLS.customizeFormFieldChoices,
        'customizeFormFieldChoices', 'loadCustomizeFormFieldChoices');

    const userCanAccessCustomizeFormFieldChoices = hasPermission('fs.application.settings.read', userData?.permissions, true);

    const searchCurrentPage = useStoreState((state) => state.search.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions) => actions.search.setSearchCurrentPage);

    const searchQueryAdditionalFilters: AdditionalSearchFilter[] = useStoreState((state) => state.search.searchQueryAdditionalFilters);

    const storeParams = useStoreActions((actions) => actions.search.setParams);


    const windowOpenDict = {
        query: JSON.stringify(searchQueryAllFields),
        searchQueryString: searchQuery,
        fieldToQueryString: fieldToQuery,
        page: searchCurrentPage,
        additionalFilters: searchQueryAdditionalFilters,
    };

    const downloadExcel = async () => {
        const url = `${window.URLS.searchQueryApiExcelDownloadResource}/?${serialize(windowOpenDict)}`;
        window.open(url, '_blank');
    };

    useEffect(() => {
        if (paramsSetter) {
            const paramsDict = {
                query_string: searchQuery,
                fields_to_query: fieldToQuery,
                all_fields: searchQueryAllFields,
            };
            storeParams(paramsDict);
            storeSearchCurrentPage(1);
        }
    }, [
        searchQuery,
        fieldToQuery,
        paramsSetter,
        searchQueryAllFields,
        storeParams,
        storeSearchCurrentPage,
        viewTypeQuery,
    ]);

    const fieldToQueryChange = (event: any) => {
        storeFieldToQuery(event.value as EventTextFieldOption);
    };

    return (
        <div className="form-row">
            <Col xs={2}>
                <Form.Group className="mb-3" controlId='searchField' >
                    {
                        userCanAccessCustomizeFormFieldChoices ? (
                            <Select <SelectOption, false>
                                value={{
                                    value: fieldToQuery,
                                    label: (customizeFormFieldChoices.find(el => el[0] === fieldToQuery)?.[1] || ''),
                                }}
                                onChange={(event) => fieldToQueryChange(event)}
                                name="fields_to_query"
                                options={customizeFormFieldChoices.map((el): SelectOption => ({
                                    value: el[0],
                                    label: el[1],
                                }))}
                                // @ts-ignore
                                getOptionLabel={(option) => {
                                    if (typeof option.label === 'string') {
                                        return option.label;
                                    }
                                    return option.label;
                                }}
                                data-cy="chart-settings-form-fields-to-query"
                            />
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement='top'
                            >
                                <Form.Control
                                    as='select'
                                    name='fields_to_query'
                                    disabled
                                />
                            </Tooltip>
                        )
                    }
                    {
                        !loadingUserData && hasPermission('fs.search.result.download', userData?.permissions, true)
                            ? (
                                <>
                                    <Button variant='info' onClick={downloadExcel} className='btn p-1 mt-2' data-cy="smart-filter-bar-btn-1">
                                        {i18n.t('download_excel.text')}
                                    </Button>
                                </>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement='top'
                                >
                                    <div style={{display: 'inline-block', cursor: 'not-allowed'}}>
                                        <Button
                                            className='btn btn-secondary p-1 mt-2'
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="smart-filter-bar-btn">
                                            {i18n.t('download_excel.text')}
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                    }
                </Form.Group>
            </Col>
            <Col xs={10}>
                <SearchBox type={SearchBoxType.Search} controlId='formGroupText' className='icon-input-group' />
            </Col>
        </div >
    );
});


export default SmartFilterBar;
